import { useEffect, useMemo, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import unescapedText from 'I18n/utils/unescapedText';
import styled from 'foundations-theming/utils';
import { Panel, PanelSection } from 'foundations-components/transitional/components/Panel';
import { PanelBody } from 'foundations-components/transitional/components/PanelBody';
import { CloseButton } from 'foundations-components/transitional/components/CloseButton';
import { Tabs, Tab } from 'foundations-components/transitional/components/Tabs';
import { ConfirmModal } from 'foundations-components/transitional/components/ConfirmModal';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { H2 } from 'foundations-components/transitional/components/H2';
import { View } from 'foundations-components/transitional/components/View';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { FloatingAlertList, FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { HR } from 'foundations-components/transitional/components/HR';
import { EmailIcon } from 'foundations-assets/icons/components/EmailIcon';
import { InfoIcon } from 'foundations-assets/icons/components/InfoIcon';
import { LockedIcon } from 'foundations-assets/icons/components/LockedIcon';
import { UserIcon } from 'foundations-assets/icons/components/UserIcon';
import { useInviteUserMutation, useListUserInvitationsQuery, useUninviteUserMutation } from '../../__generated__/chirp/com/hubspot/portals/platform/app/rpc/AccessDelegationRpc.hooks';
import { canInviteUsers, canRemoveUsers, getAssignableUserRoles, getDefaultUserRole, toDisplayRole } from '../permissions/sharePermissions';
import { getInviteErrorMessage } from '../utils/accessDelegationErrors';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function getRedirectSlug() {
  const prefixTag = document.querySelector('meta[name="x-hs-portals-prefix"]');
  const rawPrefix = (prefixTag === null || prefixTag === void 0 ? void 0 : prefixTag.getAttribute('content')) || '';
  const prefixParts = rawPrefix.split('/').filter(Boolean);
  return prefixParts.length > 0 ? `${prefixParts.join('/')}/` : '/';
}
function formatName(user) {
  var _user$firstName, _user$lastName;
  const firstName = ((_user$firstName = user.firstName) === null || _user$firstName === void 0 ? void 0 : _user$firstName.trim()) || '';
  const lastName = ((_user$lastName = user.lastName) === null || _user$lastName === void 0 ? void 0 : _user$lastName.trim()) || '';
  const fullName = `${firstName} ${lastName}`.trim();
  if (fullName) {
    return fullName;
  }
  return user.email.split('@')[0];
}
function getInitials(user) {
  var _user$firstName2, _user$lastName2;
  const parts = [(_user$firstName2 = user.firstName) === null || _user$firstName2 === void 0 ? void 0 : _user$firstName2.trim(), (_user$lastName2 = user.lastName) === null || _user$lastName2 === void 0 ? void 0 : _user$lastName2.trim()].filter(p => !!p);
  if (parts.length === 0) return null;
  return parts.map(p => p[0].toUpperCase()).join('');
}
function groupByCompany(members) {
  const grouped = new Map();
  for (const member of members) {
    var _member$company;
    const key = (_member$company = member.company) !== null && _member$company !== void 0 ? _member$company : '';
    const existing = grouped.get(key);
    if (existing) {
      existing.push(member);
    } else {
      grouped.set(key, [member]);
    }
  }
  return grouped;
}
const HoverMenuItem = styled.button.withConfig({
  displayName: "InvitePanel__HoverMenuItem"
})(({
  theme
}) => ({
  display: 'block',
  width: '100%',
  background: 'none',
  border: 'none',
  padding: `${theme.space['100']} ${theme.space['300']}`,
  cursor: 'pointer',
  textAlign: 'left',
  '&:hover': {
    backgroundColor: theme.color['fill-accent-neutral-subtle']
  }
}));
const StyledPendingButton = styled.button.withConfig({
  displayName: "InvitePanel__StyledPendingButton"
})(["background:none;border:none;padding:0;cursor:pointer;"]);
function PendingButton(props) {
  return /*#__PURE__*/_jsx(StyledPendingButton, Object.assign({
    type: "button"
  }, props, {
    children: /*#__PURE__*/_jsx(Text, {
      typography: "detail-100",
      color: "text-core-subtle",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app.invitePanel.pendingStatus"
      })
    })
  }));
}
export function InvitePanel({
  onClose,
  onSuccess,
  portalInstanceId,
  accountId,
  portalUserRole,
  objectId,
  invitationsEnabled,
  spaceName
}) {
  const [activeTab, setActiveTab] = useState(() => canInviteUsers(portalUserRole, invitationsEnabled) ? 'invite' : 'peopleWithAccess');
  const [emailInput, setEmailInput] = useState('');
  const [selectedInviteRole, setSelectedInviteRole] = useState(getDefaultUserRole());
  const [inviteEmailError, setInviteEmailError] = useState(undefined);
  const [inviteApiError, setInviteApiError] = useState(null);
  const [alertStore] = useState(() => FloatingAlertStore.newFloatingAlertStore());
  const [revokeConfirmMember, setRevokeConfirmMember] = useState(null);
  const {
    data: invitationData,
    loading: isLoadingInvitations,
    error: invitationError,
    refetch
  } = useListUserInvitationsQuery({
    variables: {
      portalInstanceId,
      accountId,
      objectId
    },
    fetchPolicy: 'cache-and-network'
  });
  const [inviteUser, {
    loading: isInvitingUser
  }] = useInviteUserMutation();
  const [uninviteUser] = useUninviteUserMutation();
  const redirectSlug = useMemo(() => getRedirectSlug(), []);
  const canInvite = canInviteUsers(portalUserRole, invitationsEnabled);
  const canRemove = canRemoveUsers(portalUserRole, invitationsEnabled);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedInviteRole(currentRole => getAssignableUserRoles(portalUserRole).includes(currentRole) ? currentRole : getDefaultUserRole());
  }, [portalUserRole]);
  const teamMembers = useMemo(() => {
    const allUsers = (invitationData === null || invitationData === void 0 ? void 0 : invitationData.invitedUsers) || [];
    return allUsers.map(user => {
      const name = formatName(user);
      return {
        id: String(user.id),
        invitationId: user.id,
        name,
        email: user.email,
        jobTitle: user.jobTitle,
        company: user.company,
        role: toDisplayRole(user.role),
        status: user.status,
        initials: getInitials(user)
      };
    });
  }, [invitationData]);
  const handleInvite = async () => {
    if (!canInvite) return;
    const trimmedEmail = emailInput.trim();
    if (!trimmedEmail) {
      setInviteEmailError(I18n.text('app.invitePanel.inputPlaceholder'));
      return;
    }
    setInviteEmailError(undefined);
    setInviteApiError(null);
    try {
      await inviteUser({
        variables: {
          invitedEmail: trimmedEmail,
          userRole: selectedInviteRole,
          redirectSlug,
          portalInstanceId,
          accountId,
          objectId
        }
      });
      setEmailInput('');
      onSuccess === null || onSuccess === void 0 || onSuccess();
      alertStore.addAlert({
        type: 'success',
        titleText: I18n.text('app.invitePanel.inviteSuccessTitle'),
        message: I18n.text('app.invitePanel.inviteSuccess', {
          email: trimmedEmail
        }),
        dismissAutoMs: 5000
      });
      void refetch().catch(() => undefined).finally(() => setActiveTab('peopleWithAccess'));
    } catch (err) {
      setInviteApiError(getInviteErrorMessage(err));
    }
  };
  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      void handleInvite();
    }
  };
  const handleRemoveConfirm = async member => {
    if (!canRemove) return;
    try {
      await uninviteUser({
        variables: {
          id: member.invitationId,
          portalInstanceId,
          accountId
        }
      });
      setRevokeConfirmMember(null);
      alertStore.addAlert({
        type: 'success',
        titleText: I18n.text('app.invitePanel.removeSuccessTitle'),
        message: I18n.text('app.invitePanel.removeSuccess', {
          name: member.name
        }),
        dismissAutoMs: 5000
      });
      void refetch().catch(() => undefined);
    } catch (_unused) {
      setRevokeConfirmMember(null);
      alertStore.addAlert({
        type: 'danger',
        titleText: I18n.text('app.invitePanel.removeErrorTitle'),
        message: I18n.text('app.invitePanel.removeError')
      });
    }
  };
  const memberGroups = groupByCompany(teamMembers);
  return /*#__PURE__*/_jsxs(Panel, {
    width: 560,
    children: [/*#__PURE__*/_jsx(CloseButton, {
      onClick: onClose,
      color: "text-core-default"
    }), /*#__PURE__*/_jsx(PanelBody, {
      children: /*#__PURE__*/_jsxs(PanelSection, {
        children: [/*#__PURE__*/_jsx(FloatingAlertList, {
          alertStore: alertStore,
          use: "contextual"
        }), /*#__PURE__*/_jsx(H2, {
          typography: "heading-100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.invitePanel.title"
          })
        }), /*#__PURE__*/_jsx(Text, {
          typography: "body-100",
          color: "text-core-default",
          marginBlockEnd: "300",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.invitePanel.subtitle"
          })
        }), /*#__PURE__*/_jsxs(Tabs, {
          selected: activeTab,
          onSelectedChange: e => setActiveTab(String(e.target.value)),
          fill: true,
          panelStyles: {
            paddingBlock: '0',
            paddingInline: '0'
          },
          children: [/*#__PURE__*/_jsx(Tab, {
            tabId: "invite",
            title: !canInvite ? /*#__PURE__*/_jsxs(Flex, {
              alignItems: "center",
              justifyContent: "center",
              gap: "100",
              children: [/*#__PURE__*/_jsx(LockedIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
                message: "app.invitePanel.tabs.invite"
              })]
            }) : /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app.invitePanel.tabs.invite"
            }),
            disabled: !canInvite,
            children: /*#__PURE__*/_jsxs(Flex, {
              flexDirection: "column",
              paddingBlockStart: "400",
              paddingBlockEnd: "500",
              children: [inviteApiError && /*#__PURE__*/_jsx(Alert, {
                type: "danger",
                titleText: inviteApiError
              }), /*#__PURE__*/_jsx(FormControl, {
                error: !!inviteEmailError,
                validationMessage: inviteEmailError,
                children: /*#__PURE__*/_jsx(TextInput, {
                  placeholder: unescapedText('app.invitePanel.inputPlaceholder'),
                  value: emailInput,
                  onChange: e => {
                    setEmailInput(e.target.value);
                    if (inviteEmailError) setInviteEmailError(undefined);
                  },
                  onKeyDown: handleKeyDown,
                  disabled: isInvitingUser,
                  type: "email"
                })
              }), /*#__PURE__*/_jsxs(Flex, {
                flexDirection: "column",
                gap: "50",
                marginBlockStart: "400",
                children: [/*#__PURE__*/_jsx(Text, {
                  typography: "heading-50",
                  color: "text-core-subtle",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "app.invitePanel.permissionHeading"
                  })
                }), /*#__PURE__*/_jsx(Text, {
                  typography: "body-100",
                  color: "text-core-default",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "app.invitePanel.permissionDescription"
                  })
                })]
              }), /*#__PURE__*/_jsxs(LoadingButton, {
                use: "primary",
                block: true,
                loading: isInvitingUser,
                onClick: () => void handleInvite(),
                disabled: isInvitingUser || !emailInput.trim().length,
                marginBlockStart: "400",
                children: [/*#__PURE__*/_jsx(EmailIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "app.invitePanel.sendInvite"
                })]
              })]
            })
          }), /*#__PURE__*/_jsx(Tab, {
            tabId: "peopleWithAccess",
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app.invitePanel.tabs.peopleWithAccess"
            }),
            children: /*#__PURE__*/_jsxs(Flex, {
              flexDirection: "column",
              paddingBlockStart: "400",
              paddingBlockEnd: "500",
              children: [!canInvite && /*#__PURE__*/_jsxs(Flex, {
                alignItems: "center",
                gap: "200",
                paddingBlock: "300",
                paddingInline: "300",
                backgroundColor: "fill-accent-neutral-subtle",
                borderRadius: "100",
                marginBlockEnd: "500",
                children: [/*#__PURE__*/_jsx(InfoIcon, {}), /*#__PURE__*/_jsx(Text, {
                  typography: "detail-100",
                  color: "text-core-default",
                  children: I18n.text('app.invitePanel.invitationsDisabledBanner', {
                    spaceName: spaceName !== null && spaceName !== void 0 ? spaceName : ''
                  })
                })]
              }), isLoadingInvitations ? /*#__PURE__*/_jsx(Flex, {
                justifyContent: "center",
                paddingBlock: "600",
                children: /*#__PURE__*/_jsx(LoadingSpinner, {})
              }) : invitationError ? /*#__PURE__*/_jsx(Flex, {
                justifyContent: "center",
                paddingBlock: "600",
                children: /*#__PURE__*/_jsx(Alert, {
                  type: "danger",
                  children: /*#__PURE__*/_jsx(Text, {
                    typography: "body-100",
                    children: I18n.text('app.invitePanel.errorLoadInvitedUsers')
                  })
                })
              }) : teamMembers.length === 0 ? /*#__PURE__*/_jsx(Flex, {
                justifyContent: "center",
                paddingBlock: "600",
                children: /*#__PURE__*/_jsx(Text, {
                  typography: "body-100",
                  color: "text-core-subtle",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "app.invitePanel.peopleWithAccessEmptyState"
                  })
                })
              }) : /*#__PURE__*/_jsx(Flex, {
                flexDirection: "column",
                gap: "700",
                children: Array.from(memberGroups.entries()).map(([company, groupMembers]) => /*#__PURE__*/_jsxs(Flex, {
                  flexDirection: "column",
                  gap: "400",
                  children: [company && /*#__PURE__*/_jsx(Text, {
                    typography: "heading-50",
                    color: "text-core-subtle",
                    textTransform: "uppercase",
                    children: company
                  }), /*#__PURE__*/_jsx(Flex, {
                    flexDirection: "column",
                    gap: "600",
                    children: groupMembers.map(member => {
                      const subtitle = [member.jobTitle, member.email].filter(Boolean).join(' • ');
                      return /*#__PURE__*/_jsxs(Flex, {
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "200",
                        children: [/*#__PURE__*/_jsxs(Flex, {
                          alignItems: "center",
                          gap: "300",
                          children: [/*#__PURE__*/_jsx(Flex, {
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "fill-accent-neutral-subtle",
                            borderRadius: "full",
                            inlineSize: "32px",
                            blockSize: "32px",
                            flexShrink: 0,
                            children: member.initials !== null ? /*#__PURE__*/_jsx(Text, {
                              typography: "detail-100",
                              color: "text-core-default",
                              children: member.initials
                            }) : /*#__PURE__*/_jsx(UserIcon, {})
                          }), /*#__PURE__*/_jsxs(Flex, {
                            flexDirection: "column",
                            gap: "50",
                            children: [/*#__PURE__*/_jsxs(Flex, {
                              alignItems: "center",
                              gap: "100",
                              children: [/*#__PURE__*/_jsx(Text, {
                                children: member.name
                              }), member.status === 'INVITED' && /*#__PURE__*/_jsx(Badge, {
                                use: "yellow",
                                children: /*#__PURE__*/_jsx(FormattedMessage, {
                                  message: "app.invitePanel.invitedBadge"
                                })
                              })]
                            }), /*#__PURE__*/_jsx(Text, {
                              typography: "detail-100",
                              color: "text-core-subtle",
                              children: subtitle
                            })]
                          })]
                        }), /*#__PURE__*/_jsx(Flex, {
                          alignItems: "center",
                          gap: "200",
                          children: /*#__PURE__*/_jsxs(_Fragment, {
                            children: [member.status === 'INVITED' && canRemove && /*#__PURE__*/_jsx(Dropdown, {
                              buttonUse: "unstyled",
                              menuWidth: 160,
                              placement: "bottom right",
                              autoPlacement: false,
                              Button: PendingButton,
                              children: /*#__PURE__*/_jsx(HoverMenuItem, {
                                type: "button",
                                onClick: () => setRevokeConfirmMember(member),
                                children: /*#__PURE__*/_jsx(Text, {
                                  typography: "body-100",
                                  color: "text-core-default",
                                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                                    message: "app.invitePanel.revokeInvite"
                                  })
                                })
                              })
                            }), member.status === 'REGISTERED' && (canRemove ? /*#__PURE__*/_jsxs(Dropdown, {
                              buttonUse: "secondary-ghost",
                              buttonSize: "extra-small",
                              buttonStyles: {
                                borderColor: 'border-core-subtle'
                              },
                              buttonText: unescapedText('app.invitePanel.roleEditor'),
                              menuWidth: 250,
                              placement: "bottom right",
                              autoPlacement: false,
                              children: [/*#__PURE__*/_jsx(View, {
                                paddingBlock: "200",
                                paddingInline: "300",
                                children: /*#__PURE__*/_jsx(Text, {
                                  typography: "detail-100",
                                  color: "text-core-subtle",
                                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                                    message: "app.invitePanel.editorInfoText"
                                  })
                                })
                              }), /*#__PURE__*/_jsx(HR, {
                                distance: "flush"
                              }), /*#__PURE__*/_jsx(HoverMenuItem, {
                                type: "button",
                                onClick: () => setRevokeConfirmMember(member),
                                children: /*#__PURE__*/_jsx(Text, {
                                  typography: "body-100",
                                  color: "text-core-default",
                                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                                    message: "app.invitePanel.removeAccess"
                                  })
                                })
                              })]
                            }) : /*#__PURE__*/_jsx(Text, {
                              typography: "detail-100",
                              color: "text-core-subtle",
                              children: /*#__PURE__*/_jsx(FormattedMessage, {
                                message: "app.invitePanel.roleEditor"
                              })
                            }))]
                          })
                        })]
                      }, member.id);
                    })
                  })]
                }, company || '__no_company__'))
              })]
            })
          })]
        })]
      })
    }), revokeConfirmMember && /*#__PURE__*/_jsx(ConfirmModal, {
      message: I18n.text('app.invitePanel.revokeConfirmTitle'),
      description: I18n.text(revokeConfirmMember.status === 'INVITED' ? 'app.invitePanel.revokeConfirmDescription' : 'app.invitePanel.removeAccessConfirmDescription', {
        name: revokeConfirmMember.name
      }),
      confirmLabel: I18n.text('app.invitePanel.removeConfirmButton'),
      confirmUse: "danger",
      rejectLabel: I18n.text('app.invitePanel.removeCancel'),
      rejectUse: "secondary-ghost",
      onReject: () => setRevokeConfirmMember(null),
      onConfirm: () => void handleRemoveConfirm(revokeConfirmMember),
      size: "auto"
    })]
  });
}